/**
 * PV Quiz Viking - Personalization
 *
 * Pure functions only (no DOM access) so the copy rules can be verified
 * independently of any markup/CSS work. Every lookup is single-axis, per
 * the brief: one answer in, one line out, assembled into a paragraph.
 */

(function (root) {
  'use strict';

  var AGE_MAP = {
    'Under 30': 'in their 20s',
    '30-39': 'in their 30s',
    '40-49': 'in their 40s',
    '50-59': 'in their 50s',
    '60+': 'in their 60s and beyond'
  };

  var GOAL_MAP = {
    'Steady Energy All Day': { goal: 'steady energy all day', closer: 'it targets the root' },
    'Reduce Belly': { goal: 'to slim down', closer: 'it works at the root' },
    'More Strength & Muscle': { goal: 'to build real strength', closer: 'it rebuilds the signal' },
    'Stronger Drive & Confidence': { goal: 'their drive back', closer: 'it restores your drive' }
  };

  var BLOCKER_MAP = {
    'Afternoon Crash': 'The afternoon crash',
    'Stubborn Fat': 'Stubborn fat',
    'Low Drive / Libido': "Something that's gone quiet",
    'Harder to Build Muscle': "Muscle that won't come back",
    'Stress & Poor Recovery': 'Stress and slow recovery'
  };

  var NOT_SURE_VALUE = 'Not sure yet';

  var BENEFIT_BULLETS_MAP = {
    'Steady Energy All Day': [
      'Support steady energy, no afternoon crash',
      "Help your body produce testosterone the way it's meant to",
      'Feel sharper, morning to night'
    ],
    'Reduce Belly': [
      'Target the hormonal drivers of stubborn fat',
      "Support a leaner body composition",
      "Break the fat-storage pattern diet alone can't"
    ],
    'More Strength & Muscle': [
      'Support strength your training can build on',
      'Recover faster between sessions',
      'Rebuild the signals muscle needs to stick'
    ],
    'Stronger Drive & Confidence': [
      'Support drive at its hormonal source',
      'Restore the confidence that comes with feeling like yourself',
      'Rebuild vitality at the root, not just the symptoms'
    ],
    'fallback': [
      'Target the hormonal drivers of stubborn fat',
      "Help your body produce testosterone the way it's meant to",
      'Support strength your training can build on'
    ]
  };

  var TIER_MAP = {
    'Just exploring': 1,
    'Ready for a simple first step': 1,
    'Ready for a full 30-day plan': 2,
    'I want the complete plan': 4
  };

  // Q1-missing fallback, verbatim from the brief.
  var FALLBACK_PARAGRAPH =
    "It's built for men who want to slim down and steady energy all day. Stubborn fat and the afternoon crash aren't on you, and it works at the root.";
  var FALLBACK_BULLETS = BENEFIT_BULLETS_MAP['fallback'];

  function lowercaseFirst(str) {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  function getAgePhrase(q3) {
    return AGE_MAP[q3] || null;
  }

  function getGoalClosePhrase(q1) {
    return GOAL_MAP[q1] || null;
  }

  // Returns null if q2 is missing/empty, { notSure: true } if "Not sure yet" was
  // the only pick, or { notSure: false, text, verb } otherwise.
  function getBlockerPhrase(q2) {
    if (!Array.isArray(q2) || q2.length === 0) return null;

    if (q2.length === 1 && q2[0] === NOT_SURE_VALUE) {
      return { notSure: true };
    }

    var phrases = q2
      .filter(function (value) {
        return value !== NOT_SURE_VALUE;
      })
      .map(function (value, index) {
        var phrase = BLOCKER_MAP[value] || value;
        return index === 0 ? phrase : lowercaseFirst(phrase);
      });

    if (phrases.length === 0) return null;

    var joined;
    if (phrases.length === 1) {
      joined = phrases[0];
    } else if (phrases.length === 2) {
      joined = phrases[0] + ' and ' + phrases[1];
    } else {
      joined = phrases.slice(0, -1).join(', ') + ', and ' + phrases[phrases.length - 1];
    }

    return {
      notSure: false,
      text: joined,
      verb: phrases.length === 1 ? "isn't" : "aren't"
    };
  }

  function assembleParagraph(answers) {
    answers = answers || {};
    var goalClose = getGoalClosePhrase(answers.q1);

    if (!goalClose) {
      return FALLBACK_PARAGRAPH;
    }

    var age = getAgePhrase(answers.q3);
    var firstSentence = age
      ? "It's built for men " + age + ' who want ' + goalClose.goal + '.'
      : "It's built for men who want " + goalClose.goal + '.';

    var blockerInfo = getBlockerPhrase(answers.q2);

    var secondSentence = null;
    if (blockerInfo && blockerInfo.notSure) {
      secondSentence = "Whatever's off, it's not on you, and " + goalClose.closer + '.';
    } else if (blockerInfo) {
      secondSentence = blockerInfo.text + ' ' + blockerInfo.verb + ' on you, and ' + goalClose.closer + '.';
    }

    return secondSentence ? firstSentence + ' ' + secondSentence : firstSentence;
  }

  function getBenefitBullets(q1) {
    return BENEFIT_BULLETS_MAP[q1] || FALLBACK_BULLETS;
  }

  // Only one line confirmed so far - same subtitle for every Q1 answer until
  // the other 3 variants are provided. SUBTITLE_MAP is ready to take
  // per-answer entries the same shape as GOAL_MAP without touching callers.
  var FALLBACK_SUBTITLE = 'Not laziness. A fuel problem, and that changes things.';
  var SUBTITLE_MAP = {};

  function getSubtitle(q1) {
    return SUBTITLE_MAP[q1] || FALLBACK_SUBTITLE;
  }

  // Placeholder pass-through until real body-type icon assets exist (Screen 13 work).
  function getBodyIcon(q7) {
    return q7 || null;
  }

  function getRecommendedTier(q9) {
    return TIER_MAP[q9] || null;
  }

  root.PVQuizVikingPersonalization = {
    getAgePhrase: getAgePhrase,
    getGoalClosePhrase: getGoalClosePhrase,
    getBlockerPhrase: getBlockerPhrase,
    assembleParagraph: assembleParagraph,
    getBenefitBullets: getBenefitBullets,
    getSubtitle: getSubtitle,
    getBodyIcon: getBodyIcon,
    getRecommendedTier: getRecommendedTier
  };
})(typeof window !== 'undefined' ? window : this);
